import { Editor, Node, mergeAttributes } from "@tiptap/core";

import { Signal } from "@/data/util/Signal";

export interface AbsoluteTextBoxOptions {
    HTMLAttributes: Record<string, any>;
}

export const AbsoluteTextBoxEvents = {
    delete: new Signal(),
};

export const AbsoluteTextBox = Node.create<AbsoluteTextBoxOptions>({
    name: "absoluteTextBox",
    group: "block",
    content: "block+",
    atom: false,
    selectable: true,
    draggable: false,

    addAttributes() {
        return {
            top: { default: "0px" },
            left: { default: "0px" },
            width: { default: "150px" },
            height: { default: "100px" },
        };
    },

    parseHTML() {
        return [
            {
                tag: "div[data-absolute-box]",
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-absolute-box": "true",
                class: "absolute-text-box",
                style: `
          position: absolute;
          top: ${HTMLAttributes.top};
          left: ${HTMLAttributes.left};
          width: ${HTMLAttributes.width};
          height: ${HTMLAttributes.height};
        `,
            }),
            0, // very important: this is the slot for contentDOM
        ];
    },

    addNodeView() {
        return ({ node, getPos, view }) => {
            const wrapper = document.createElement("div");
            wrapper.classList.add("absolute-text-box");
            wrapper.setAttribute("data-absolute-box", "true");
            wrapper.style.position = "absolute";
            wrapper.style.top = node.attrs.top;
            wrapper.style.left = node.attrs.left;
            wrapper.style.width = node.attrs.width;
            wrapper.style.height = node.attrs.height;

            const handle = document.createElement("div");
            handle.classList.add("drag-handle");

            const contentDOM = document.createElement("div");
            contentDOM.classList.add("content");
            contentDOM.contentEditable = "true";

            const removeButton = document.createElement("div");
            removeButton.classList.add("remove-button");
            removeButton.innerHTML = "&times;";
            removeButton.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();

                const pos = getPos?.();
                if (typeof pos === "number") {
                    const tr = view.state.tr.delete(pos, pos + node.nodeSize);
                    view.dispatch(tr);
                    AbsoluteTextBoxEvents.delete.dispatch();
                }
            });

            const resizeHandle = document.createElement("div");
            resizeHandle.classList.add("resize-handle");

            wrapper.appendChild(resizeHandle);
            wrapper.appendChild(removeButton);
            wrapper.appendChild(handle);
            wrapper.appendChild(contentDOM);

            let isDragging = false;
            let offsetX = 0;
            let offsetY = 0;
            let currentPos: number | undefined;

            function fixPosition(left: number, top: number, width: number, height: number) {
                const minLeft = 5;
                const minTop = -5;
                const maxLeft = 794 - width - 5;
                const maxTop = 1123 - height;

                if (left < minLeft) {
                    left = minLeft;
                }
                if (left > maxLeft) {
                    left = maxLeft;
                }
                if (top < minTop) {
                    top = minTop;
                }
                if (top > maxTop) {
                    top = maxTop;
                }

                return { left, top };
            }

            function fixDimensions(left: number, top: number, width: number, height: number) {
                const maxWidth = 794 - left - 5;
                const maxHeight = 1123 - top;

                if (width > maxWidth) {
                    width = maxWidth;
                }
                if (height > maxHeight) {
                    height = maxHeight;
                }

                return { width, height };
            }

            handle.addEventListener("mousedown", (e) => {
                isDragging = true;
                currentPos = getPos?.();
                if (typeof currentPos !== "number") return;

                offsetX = e.clientX - parseInt(wrapper.style.left);
                offsetY = e.clientY - parseInt(wrapper.style.top);

                e.preventDefault();
                e.stopPropagation();

                const onMouseMove = (e: MouseEvent) => {
                    if (!isDragging) return;

                    let newLeft = e.clientX - offsetX;
                    let newTop = e.clientY - offsetY;

                    const { left, top } = fixPosition(
                        newLeft,
                        newTop,
                        parseInt(wrapper.style.width),
                        parseInt(wrapper.style.height)
                    );
                    newLeft = left;
                    newTop = top;

                    wrapper.style.left = `${newLeft}px`;
                    wrapper.style.top = `${newTop}px`;

                    if (typeof currentPos !== "number") {
                        return;
                    }

                    view.dispatch(
                        view.state.tr.setNodeMarkup(currentPos, undefined, {
                            ...node.attrs,
                            left: wrapper.style.left,
                            top: wrapper.style.top,
                        })
                    );
                };

                const onMouseUp = () => {
                    if (!isDragging) return;
                    isDragging = false;

                    window.removeEventListener("mousemove", onMouseMove);
                    window.removeEventListener("mouseup", onMouseUp);

                    if (typeof currentPos !== "number") return;

                    view.dispatch(
                        view.state.tr.setNodeMarkup(currentPos, undefined, {
                            ...node.attrs,
                            left: wrapper.style.left,
                            top: wrapper.style.top,
                        })
                    );
                };

                window.addEventListener("mousemove", onMouseMove);
                window.addEventListener("mouseup", onMouseUp);
            });

            resizeHandle.addEventListener("mousedown", (e) => {
                e.preventDefault();
                e.stopPropagation();

                const startX = e.clientX;
                const startY = e.clientY;
                const startWidth = wrapper.offsetWidth;
                const startHeight = wrapper.offsetHeight;

                const pos = getPos?.();
                if (typeof pos !== "number") return;

                const onMouseMove = (e: MouseEvent) => {
                    let newWidth = startWidth + (e.clientX - startX);
                    let newHeight = startHeight + (e.clientY - startY);

                    const { width, height } = fixDimensions(
                        parseInt(wrapper.style.left),
                        parseInt(wrapper.style.top),
                        newWidth,
                        newHeight
                    );
                    newWidth = width;
                    newHeight = height;

                    wrapper.style.width = `${newWidth}px`;
                    wrapper.style.height = `${newHeight}px`;

                    // dispatch za każdym razem — ale tylko jeśli pos jest ważne
                    if (typeof pos === "number") {
                        view.dispatch(
                            view.state.tr.setNodeMarkup(pos, undefined, {
                                ...node.attrs,
                                width: `${newWidth}px`,
                                height: `${newHeight}px`,
                            })
                        );
                    }
                };

                const onMouseUp = () => {
                    window.removeEventListener("mousemove", onMouseMove);
                    window.removeEventListener("mouseup", onMouseUp);
                };

                window.addEventListener("mousemove", onMouseMove);
                window.addEventListener("mouseup", onMouseUp);
            });

            return {
                dom: wrapper,
                contentDOM,
                update(updatedNode) {
                    if (
                        updatedNode.attrs.left === node.attrs.left &&
                        updatedNode.attrs.top === node.attrs.top &&
                        updatedNode.attrs.width === node.attrs.width &&
                        updatedNode.attrs.height === node.attrs.height
                    ) {
                        return true; // nic się nie zmieniło, nie rób nic
                    }

                    return false; // wymuś rekonstrukcję jeśli się coś zmieniło
                },
            };
        };
    },
});

export function addAbsoluteTextBoxWithContent(editor: Editor, content: any) {
    const editorWidth = 794; // taken from VoucherHtmlEditor.vue

    const boxWidth = 200; // px
    const boxHeight = 100; // px
    const randomInRange20X = () => Math.floor(Math.random() * 41) - 20;
    const randomInRange20Y = () => Math.floor(Math.random() * 41) - 20;

    const top = 100 + randomInRange20Y() + "px";
    const centerLeft = Math.max((editorWidth - boxWidth) / 2, 0) + randomInRange20X() + "px";

    editor
        .chain()
        .focus("end")
        .insertContent({
            type: "absoluteTextBox",
            attrs: {
                top: top,
                left: centerLeft,
                width: boxWidth + "px",
                height: boxHeight + "px",
            },
            content,
        })
        .run();
}
